import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { services, projects } from "@/lib/portfolio";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { internalToPublicSlug } from "@/lib/work-data";

function findService(slug: string) {
  return services.find((s) => s.slug === slug);
}

export const Route = createFileRoute("/services_/$slug")({
  loader: ({ params }) => {
    const service = findService(params.slug);
    if (!service) throw notFound();
    return { service };
  },
  head: ({ params, loaderData }) => {
    const s = loaderData?.service ?? findService(params.slug);
    if (!s) {
      return {
        meta: [
      { name: "twitter:card", content: "summary_large_image" },

          { title: "Service not found | Scott Cunningham" },
          { name: "description", content: "This service could not be found. Explore Scott Cunningham’s content strategy and AI workflow services." },
          { property: "og:title", content: "Service not found | Scott Cunningham" },
          { property: "og:description", content: "This service could not be found. Explore Scott Cunningham’s content strategy and AI workflow services." },
          { property: "og:type", content: "website" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const url = `https://scottcbusiness.com/services/${s.slug}`;
    const title = `${s.title} | Scott Cunningham`;
    const description = `${s.body} ${s.tagline}`;
    return {
      meta: [
        { title },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Service",
                "@id": `${url}#service`,
                name: s.title,
                description: s.longBody,
                url,
                serviceType: s.title,
                areaServed: "Worldwide",
                provider: { "@id": "https://scottcbusiness.com/#person" },
                audience: { "@type": "Audience", audienceType: s.idealFor },
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "Home",
                    item: "https://scottcbusiness.com/",
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: "Services",
                    item: "https://scottcbusiness.com/services",
                  },
                  { "@type": "ListItem", position: 3, name: s.title, item: url },
                ],
              },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <section className="mx-auto max-w-3xl px-6 py-32 text-center">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">404</p>
      <h1 className="mt-4 font-display text-4xl">Service not found.</h1>
      <Link to="/services" className="mt-8 inline-block underline">
        Back to all services
      </Link>
    </section>
  ),
  component: ServiceDetailPage,
});

function ServiceDetailPage() {
  const { service } = Route.useLoaderData();
  const relatedCase = projects.find((p) => p.slug === service.relatedCaseSlug);
  const otherServices = services.filter((s) => s.slug !== service.slug);

  return (
    <>
      <section className="relative overflow-hidden border-b border-border grain">
        <div className="blob float-slow -left-32 -top-24 h-[420px] w-[420px] bg-[var(--color-primary)]" />
        <div
          className="blob float-slow right-[-120px] top-20 h-[360px] w-[360px] bg-[var(--color-violet)]"
          style={{ animationDelay: "-3s" }}
        />
        <div className="relative mx-auto max-w-5xl px-6 pt-16 pb-12 lg:px-10 lg:pt-20 lg:pb-16">
          <Breadcrumbs
            crumbs={[
              { label: "Home", to: "/" },
              { label: "Services", to: "/services" },
              { label: service.title },
            ]}
          />
          <p className="mt-8 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            Service · {service.n}
          </p>
          <h1 className="mt-4 font-display text-[clamp(2.25rem,6vw,5rem)] leading-[0.95]">
            {service.title}
          </h1>
          <p className="mt-6 max-w-2xl font-display text-xl leading-snug md:text-2xl">
            {service.tagline}
          </p>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground">
            {service.longBody}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 rounded-full bg-gradient-hero px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-primary-foreground shadow-lg shadow-primary/30 transition-transform hover:-translate-y-0.5"
            >
              Start a project{" "}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
            </Link>
            <Link
              to="/work"
              className="inline-flex items-center gap-2 rounded-full border-2 border-foreground px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-transform hover:-translate-y-0.5"
            >
              See selected work
            </Link>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-gradient-acid">
        <div className="mx-auto max-w-5xl px-6 py-16 lg:px-10 lg:py-24">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-4">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                What's included
              </p>
              <h2 className="mt-4 font-display text-3xl leading-tight md:text-4xl">
                Deliverables.
              </h2>
            </div>
            <ul className="md:col-span-8 divide-y divide-foreground/15 border-y border-foreground/15">
              {service.deliverables.map((d: string, i: number) => (
                <li key={d} className="flex items-start gap-4 py-4">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                    0{i + 1}
                  </span>
                  <span className="text-base leading-relaxed">{d}</span>
                </li>
              ))}
            </ul>
          </div>
          <dl className="mt-12 grid stat-grid gap-px rounded-md border-2 border-foreground bg-foreground shadow-pop md:grid-cols-2">
            <div className="bg-background p-6">
              <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Ideal for
              </dt>
              <dd className="mt-3 font-display text-xl">{service.idealFor}</dd>
            </div>
            <div className="bg-background p-6">
              <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Engagement
              </dt>
              <dd className="mt-3 font-display text-xl">{service.engagement}</dd>
            </div>
          </dl>
        </div>
      </section>

      {relatedCase && (
        <section className="border-b border-border">
          <div className="mx-auto max-w-5xl px-6 py-16 lg:px-10 lg:py-24">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
              Related case study
            </p>
            <Link
              to="/work/$slug"
              params={{ slug: internalToPublicSlug[relatedCase.slug] ?? relatedCase.slug }}
              className="group mt-6 grid gap-6 rounded-md border-2 border-foreground bg-background p-8 shadow-pop transition-transform hover:-translate-y-0.5 md:grid-cols-12 md:p-10"
            >
              <div className="md:col-span-8">
                <p
                  className="font-mono text-[11px] uppercase tracking-[0.22em]"
                  style={{ color: "var(--color-electric)" }}
                >
                  {relatedCase.sector}
                </p>
                <h3 className="mt-3 font-display text-3xl leading-tight md:text-4xl">
                  {relatedCase.name}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  {relatedCase.summary}
                </p>
              </div>
              <div className="flex items-end justify-end md:col-span-4">
                <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                  {service.relatedCaseLabel}{" "}
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-5xl px-6 py-16 lg:px-10 lg:py-24">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            Other ways to work together
          </p>
          <ul className="mt-6 grid gap-4 md:grid-cols-3">
            {otherServices.map((o) => (
              <li key={o.slug}>
                <Link
                  to="/services/$slug"
                  params={{ slug: o.slug }}
                  className="group block h-full rounded-md border-2 border-foreground bg-background p-6 shadow-pop transition-transform hover:-translate-y-0.5"
                >
                  <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                    {o.n}
                  </p>
                  <h3 className="mt-3 font-display text-xl leading-tight">{o.title}</h3>
                  <span className="mt-4 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em]">
                    Explore {o.title}{" "}
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:rotate-45" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-gradient-acid">
        <div className="mx-auto max-w-5xl px-6 py-16 lg:px-10 lg:py-24">
          <div className="rounded-md border-2 border-foreground bg-background p-8 shadow-pop md:p-12">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
              Engagement
            </p>
            <h3 className="mt-4 max-w-2xl font-display text-3xl md:text-4xl">
              Ready to scope <em className="italic text-gradient">{service.title.toLowerCase()}</em>
              ?
            </h3>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-hero px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-primary-foreground shadow-lg shadow-primary/30 transition-transform hover:-translate-y-0.5"
            >
              Start the conversation <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
